import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Typography, Space, Row, Col, Table, notification } from "antd";
import { PlusOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import metaData from "../../../data/metaData";

//
import ShopStatus from "../../../components/Table/ShopStatus";
import ActionHeader from "../../../components/Table/ActionHeader";
import DelaySearch from "../../../components/Table/DelaySearch";
import FilterDropdown from "../../../components/Table/FIlterDropdown";
import Meta from "../../../components/Meta";

//
import useAuthStore from "../../../states/useAuth";
import useShopQuery from "../../../hooks/useQuery/useShop";
import ShopLogo from "../../../components/Shop/Logo";

const { Title, Text } = Typography;

const Shop = () => {
  const history = useHistory();
  const location = useLocation();

  const { auth, user } = useAuthStore();

  const {
    data: shops = [],
    isFetching: isFetchingShops,
    refetch: refetchShops,
  } = useShopQuery("shops/query", {
    companyId: user?.companyId,
    select: "-gallery -paymentMethod -email -website -openHour -description -phone",
    populate: {
      path: "brand",
      select: "logo",
    },
  });

  //Filter
  const [searchValue, setSearchValue] = useState();

  //Table
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(shops.length);

  const pageRef = useRef();

  const filterData = (searchValue, data) => {
    if (!searchValue) return data;

    const lowerSearchValue = searchValue.toLowerCase();

    return data.filter((record) => {
      const id = record?.id?.toLowerCase() || "";
      const shopName = record?.name?.toLowerCase() || "";

      return id.includes(lowerSearchValue) || shopName.includes(lowerSearchValue);
    });
  };

  const filteredData = useMemo(() => {
    // Filter data based on searchValue
    let filtered = filterData(searchValue, shops);

    return filtered.sort((a, b) => {
      // Sort by status first (pending over approved), then by lastModify in descending order
      // if (a.status !== b.status) {
      //   return a.status === "pending" ? -1 : 1;
      // }
      return new Date(b.time.lastModify) - new Date(a.time.lastModify);
    });
  }, [shops, searchValue]);

  const columns = [
    // {
    //   title: "RequestId",
    //   dataIndex: "id",
    //   key: "id",
    //   render: (text, record) => <Paragraph copyable={{ text: record?.id }}>{record?.id}</Paragraph>,
    //   sorter: (a, b) => (a?.id).localeCompare(b?.id),
    // },
    {
      title: "Shop",
      dataIndex: "name",
      key: "name",
      width: 300,
      render: (text, record) => {
        return (
          <Meta
            image={
              <ShopLogo
                style={{ width: 40, height: 40, objectFit: "cover" }}
                logo={record?.logo}
                brandLogo={record?.brand?.logo}
              />
            }
            title={record?.name}
            description={c.getDisplayAddress(record?.address)}
            maxWidth={200}
          />
        );
      },
      sorter: (a, b) => (a?.name).localeCompare(b?.name),
    },
    {
      title: "Status",
      dataIndex: "isDisable",
      key: "isDisable",
      width: 200,
      render: (text, record) => <ShopStatus shop={record} />,
      sorter: (a, b) => (a?.isDisable).localeCompare(b?.isDisable),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (text, record) => (
        <Space
          style={{
            fontSize: "1rem",
          }}
        >
          <a
            onClick={() => {
              history.push("/merchantUser/shop/" + record?.id);
            }}
          >
            detail
          </a>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
  };

  const handleActionClick = (key) => {
    log.debug(key);
  };

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Shop</Title>
        <div>
          <Button
            size="large"
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginRight: "8px" }}
            onClick={() => {
              history.push("/merchantUser/shop/request/new");
            }}
          >
            Add Shop
          </Button>
        </div>
      </div>

      <Row justify="space-between" style={{ width: "100%", marginBottom: "2rem" }}>
        <DelaySearch
          style={{ width: "20rem" }}
          placeholder="Search shop name / ID"
          onSearch={(value) => {
            log.debug(value);
            setSearchValue(value);
          }}
          isDelay={false}
        />
      </Row>

      {/* Filter Group */}
      {/* {activeSegment === "all" ? (
        <Card bordered style={{ marginBottom: "20px", background: "var(--ion-color-step-50)" }}>
          <Row>
            <FilterDropdown
              label="Status"
              defaultValue="all"
              options={[
                { value: "all", label: "All Status" },
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
            />
          </Row>
        </Card>
      ) : null} */}

      <ActionHeader
        pagination={pagination}
        pageSize={20}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        count={filteredDataCount}
        onActionClick={handleActionClick}
      />

      <Table
        className="white-table-header"
        // rowSelection={rowSelection}
        rowSelection={undefined} //disabled multiple selection
        sticky
        columns={columns}
        loading={isFetchingShops}
        dataSource={filteredData}
        pagination={{
          current: pagination,
          pageSize: 20,
          position: ["none", "bottomCenter"],
          showSizeChanger: false,
        }}
        onChange={handleTableChange} // This handles the next/prev page
      />
    </div>
  );
};

export default Shop;
